import {Separator, Tooltip} from 'platform/components';
import {
  Box,
  Clickable,
  Display,
  getCssSize,
  Hide,
  HStack,
  Icon,
  Show,
  Text,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {isLastIndexInArray} from 'shared/web';

import {WarehouseMetricsVariant} from './types/WarehouseMetricsVariant';
import {getColumns} from './utils/getColumns';

interface WarehouseMetricsProps {
  columns: ReturnType<typeof getColumns>;
  isRedirecting: boolean;
  /**
   * `compact` renders each metric as a grey one-line tile so the wide tile can spend its height on
   * MOT rows: 4px padding + a 24px `base` line = 32px, which leaves room for five rows per column.
   */
  variant?: WarehouseMetricsVariant;
  onOpenMetric: (metric: 'warehouseTotal' | 'inSale' | 'inBuying') => void;
}

export function WarehouseMetrics(props: WarehouseMetricsProps) {
  return (
    <>
      <Show onMobile onTablet>
        <VStack spacing={3}>
          {props.columns.map((column) => (
            <Box key={`${column.id}_${column.label}`} width="100%">
              <HoverBox>
                <Clickable
                  onClick={() => props.onOpenMetric(column.id)}
                  isDisabled={props.isRedirecting}
                >
                  <HStack spacing={3} align="center" justify="space-between">
                    <HStack align="center" spacing={1}>
                      <Text color="secondary" size="xSmall">
                        {column.label}
                      </Text>
                      <Tooltip description={column.tooltip}>
                        <Icon value="action/help" size={4} tabIndex={-1} />
                      </Tooltip>
                    </HStack>
                    <Display size={2}>{String(column.value)}</Display>
                  </HStack>
                </Clickable>
              </HoverBox>
            </Box>
          ))}
        </VStack>
      </Show>
      <Show onNotebook onDesktop>
        {match(props.variant ?? 'default')
          .with('compact', () => (
            <HStack spacing={2}>
              {props.columns.map((column) => (
                <Box key={`${column.id}_${column.label}`} flex={1} minWidth={0}>
                  <MetricTile>
                    <Clickable
                      onClick={() => props.onOpenMetric(column.id)}
                      isDisabled={props.isRedirecting}
                    >
                      <HStack align="center" spacing={2} justify="space-between" wrap="nowrap">
                        <HStack align="center" spacing={1} minWidth={0} wrap="nowrap">
                          <Text color="secondary" size="xSmall" noWrap>
                            {column.label}
                          </Text>
                          <Tooltip description={column.tooltip}>
                            <Icon value="action/help" size={4} tabIndex={-1} />
                          </Tooltip>
                        </HStack>
                        <Text size="base" alternative noWrap>
                          {String(column.value)}
                        </Text>
                      </HStack>
                    </Clickable>
                  </MetricTile>
                </Box>
              ))}
            </HStack>
          ))
          .with('default', () => (
            <HStack>
              {props.columns.map((column, index) => (
                <Fragment key={`${column.id}_${column.label}`}>
                  <HStack basis="100%" align="stretch" spacing={4}>
                    <HoverBox>
                      <Clickable
                        onClick={() => props.onOpenMetric(column.id)}
                        isDisabled={props.isRedirecting}
                      >
                        <VStack spacing={4} justify="space-between" grow={1}>
                          <HStack align="center" spacing={1}>
                            <Text color="secondary" size="xSmall">
                              {column.label}
                            </Text>
                            <Tooltip description={column.tooltip}>
                              <Icon value="action/help" size={4} tabIndex={-1} />
                            </Tooltip>
                          </HStack>
                          <Display size={1}>{String(column.value)}</Display>
                        </VStack>
                      </Clickable>
                    </HoverBox>
                  </HStack>
                  <Hide when={isLastIndexInArray(props.columns, index)}>
                    <Separator spacing={4} orientation="vertical" />
                  </Hide>
                </Fragment>
              ))}
            </HStack>
          ))
          .exhaustive()}
      </Show>
    </>
  );
}

const HoverBox = styled.div`
  padding: ${getCssSize(2)};
  width: 100%;
  border-radius: ${({theme}) => theme.radii.medium};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[40][40]};
  }
`;

// The compact tile already sits on a grey base, so its hover needs the fully opaque next palette
// step to stay visible.
const MetricTile = styled.div`
  padding: ${getCssSize(1)} ${getCssSize(2)};
  width: 100%;
  background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  border-radius: ${({theme}) => theme.radii.small};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[40][100]};
  }
`;
